import { FC, ReactNode, useMemo } from 'react';
import { Stack, Typography } from '@mui/material';

import { EMlpIconTypes, EMplVariantTypes } from '@mopla/ui';

import { IconWrapper } from '../styled';

import { AnimatedMessageSlot } from './AnimatedMessageSlot';
import { ContentWrapper, InfoWrapper } from './styled';

export type TBookingInfoIconMessage = {
	type: EMlpIconTypes;
	variant: EMplVariantTypes;
	message: ReactNode;
	/**
	 * Stable identity of the message, used to animate a replacement. Omit it
	 * for messages that never change — they then render without animation.
	 */
	key?: string;
};

/** Priority order: Error > Warning > Info > Success > Information/Default */
const VARIANT_PRIORITY: Record<EMplVariantTypes, number> = {
	[EMplVariantTypes.Error]: 1,
	[EMplVariantTypes.Warning]: 2,
	[EMplVariantTypes.Info]: 3,
	[EMplVariantTypes.Success]: 4,
	[EMplVariantTypes.Default]: 5,
};

interface IBookingInfoProps {
	icon: ReactNode;
	title: string;
	subtitle: ReactNode;
	rightContent?: ReactNode;
	infoMessages?: TBookingInfoIconMessage[];
	testId?: string;
}

export const BookingInfo: FC<IBookingInfoProps> = ({
	icon,
	title,
	subtitle,
	rightContent,
	infoMessages,
	testId,
}) => {
	const sortedMessages = useMemo(() => {
		if (!infoMessages) return [];
		return [...infoMessages].sort(
			(a, b) => VARIANT_PRIORITY[a.variant] - VARIANT_PRIORITY[b.variant]
		);
	}, [infoMessages]);

	return (
		<Stack spacing={2} data-testid={testId}>
			<ContentWrapper>
				{icon && <IconWrapper>{icon}</IconWrapper>}
				<InfoWrapper>
					<Stack spacing={0.5}>
						<Typography
							variant="b1-sb"
							color="primary"
							data-testid="bookingInfo-title-text"
						>
							{title}
						</Typography>
						{subtitle}
					</Stack>
					<Stack spacing={0.5}>{rightContent}</Stack>
				</InfoWrapper>
			</ContentWrapper>
			{sortedMessages.map((msg, index) => (
				<AnimatedMessageSlot key={index} message={msg} />
			))}
		</Stack>
	);
};
